import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
} from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { API_BASE_URL } from '../../../config/apiConfig';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
} from '../../../components/ui/Icons';

const STATUS_BADGE = {
  sent: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  skipped: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
};

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'sent':
      return <CheckCircleIcon className="h-4 w-4 text-green-500 shrink-0" />;
    case 'failed':
      return <XCircleIcon className="h-4 w-4 text-red-500 shrink-0" />;
    case 'skipped':
      return (
        <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500 shrink-0" />
      );
    default:
      return <ClockIcon className="h-4 w-4 text-gray-500 shrink-0" />;
  }
};

const ReportScheduleHistoryModal = ({
  isOpen,
  onClose,
  scheduleId,
  scheduleName,
}) => {
  const { theme } = useTheme();
  const [runs, setRuns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snapshotRun, setSnapshotRun] = useState(null);

  useEffect(() => {
    if (!isOpen || !scheduleId) return;
    let aborted = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(
          `${API_BASE_URL}/report-schedules/${scheduleId}/runs?limit=100`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          },
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(
            data?.message || `Failed to load runs (${res.status})`,
          );
        }
        const data = await res.json();
        if (!aborted) setRuns(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!aborted) setError(err.message || 'Failed to load runs');
      } finally {
        if (!aborted) setIsLoading(false);
      }
    };
    load();
    return () => {
      aborted = true;
    };
  }, [isOpen, scheduleId]);

  useEffect(() => {
    if (!isOpen) setSnapshotRun(null);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        className="max-h-[90vh] flex flex-col overflow-hidden"
      >
        <ModalHeader onClose={onClose} className="shrink-0">
          <ModalTitle>
            Run History{scheduleName ? ` — ${scheduleName}` : ''}
          </ModalTitle>
        </ModalHeader>

        <ModalContent className="flex-1 min-h-0 overflow-hidden p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : error ? (
            <div className="p-6">
              <div
                className={cn(
                  'text-sm p-3 rounded border',
                  'text-red-700 bg-red-50 border-red-200',
                  'dark:text-red-200 dark:bg-red-950/40 dark:border-red-800',
                )}
              >
                {error}
              </div>
            </div>
          ) : runs.length === 0 ? (
            <div className="text-center py-12 px-6">
              <ClockIcon className={cn('mx-auto h-12 w-12', theme.text.muted)} />
              <h3 className={cn('mt-2 text-sm font-medium', theme.text.primary)}>
                No Runs Yet
              </h3>
              <p className={cn('mt-1 text-sm', theme.text.secondary)}>
                This schedule hasn&apos;t fired yet.
              </p>
            </div>
          ) : (
            <div className="max-h-[min(65vh,calc(90vh-9rem))] overflow-auto advanced-table-scroll">
              <table className="min-w-full text-sm">
                <thead
                  className={cn(
                    'sticky top-0 z-10 border-b',
                    theme.colors.backgroundAlt,
                    theme.border.light,
                  )}
                >
                  <tr>
                    {['When', 'Status', 'Recipients', 'Detail', 'Snapshot'].map(
                      (label) => (
                        <th
                          key={label}
                          className={cn(
                            'px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap',
                            theme.text.secondary,
                          )}
                        >
                          {label}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody className={cn('divide-y', theme.border.subtle)}>
                  {runs.map((run) => (
                    <tr
                      key={run.id}
                      className={cn(
                        'align-top',
                        'hover:bg-black/[0.03] dark:hover:bg-white/[0.04]',
                      )}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className={cn('font-medium', theme.text.primary)}>
                          {run.startedAt
                            ? new Date(run.startedAt).toLocaleString()
                            : '—'}
                        </div>
                        {run.finishedAt && (
                          <div className={cn('text-xs mt-0.5', theme.text.muted)}>
                            Finished:{' '}
                            {new Date(run.finishedAt).toLocaleTimeString()}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <StatusIcon status={run.status} />
                          <span
                            className={cn(
                              'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize',
                              STATUS_BADGE[run.status] ||
                                'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
                            )}
                          >
                            {run.status}
                          </span>
                        </div>
                      </td>
                      <td className={cn('px-4 py-3', theme.text.primary)}>
                        {Array.isArray(run.recipients) &&
                        run.recipients.length > 0 ? (
                          <div
                            className="max-w-[12rem] truncate"
                            title={run.recipients.join(', ')}
                          >
                            {run.recipients.length} recipient
                            {run.recipients.length === 1 ? '' : 's'}
                          </div>
                        ) : (
                          <span className={theme.text.muted}>—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {run.errorMessage ? (
                          <div
                            className={cn(
                              'max-w-[16rem] line-clamp-3 break-words',
                              run.status === 'sent'
                                ? 'text-yellow-700 dark:text-yellow-300'
                                : 'text-red-600 dark:text-red-300',
                            )}
                            title={run.errorMessage}
                          >
                            {run.errorMessage}
                          </div>
                        ) : (
                          <span className={theme.text.muted}>—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {run.reportSnapshot ? (
                          <button
                            onClick={() => setSnapshotRun(run)}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-xs font-medium"
                            type="button"
                          >
                            View JSON
                          </button>
                        ) : (
                          <span className={theme.text.muted}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ModalContent>

        <ModalFooter className="shrink-0">
          <div className="flex w-full items-center justify-between gap-3">
            <span className={cn('text-xs', theme.text.muted)}>
              {!isLoading && !error
                ? `${runs.length} run${runs.length === 1 ? '' : 's'}`
                : ''}
            </span>
            <Button onClick={onClose} variant="secondary">
              Close
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      {snapshotRun && (
        <Modal
          isOpen={!!snapshotRun}
          onClose={() => setSnapshotRun(null)}
          size="lg"
          className="max-h-[90vh] flex flex-col overflow-hidden"
        >
          <ModalHeader
            onClose={() => setSnapshotRun(null)}
            className="shrink-0"
          >
            <ModalTitle>
              Report Snapshot
              {snapshotRun.startedAt
                ? ` — ${new Date(snapshotRun.startedAt).toLocaleString()}`
                : ''}
            </ModalTitle>
          </ModalHeader>
          <ModalContent className="flex-1 min-h-0 overflow-hidden p-4">
            <pre
              className={cn(
                'rounded-lg p-3 text-xs leading-relaxed',
                'max-h-[min(70vh,calc(90vh-9rem))] overflow-auto',
                'bg-slate-900 text-slate-100',
              )}
            >
              {JSON.stringify(snapshotRun.reportSnapshot, null, 2)}
            </pre>
          </ModalContent>
          <ModalFooter className="shrink-0">
            <Button onClick={() => setSnapshotRun(null)} variant="secondary">
              Close
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default ReportScheduleHistoryModal;
